import { useCallback, useEffect, useState } from 'react';

/**
 * Shell chrome state that must OUTLIVE a route change.
 *
 * The old shell held all of this in plain `useState` inside a layout
 * that each page re-mounted, so collapsing the rail lasted exactly
 * one click. Persisting to localStorage fixes both that and a full
 * page reload, and is read lazily so the first paint is already
 * correct (no expand→collapse flash).
 */

function readJSON<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — state simply won't persist */
  }
}

const COLLAPSED_KEY = 'dc.shell.sidebarCollapsed';
const GROUPS_KEY = 'dc.shell.openNavGroups';

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() =>
    readJSON<boolean>(COLLAPSED_KEY, false),
  );
  useEffect(() => writeJSON(COLLAPSED_KEY, collapsed), [collapsed]);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return { collapsed, setCollapsed, toggle };
}

/**
 * Which accordion groups are open. Persisted as a list of ids.
 *
 * `ensureOpen` is called when the route lands inside a group, so
 * navigating to a Finance page opens Finance — but the user closing
 * it afterwards sticks, because we only ever ADD here.
 */
export function useOpenNavGroups() {
  const [open, setOpen] = useState<string[]>(() =>
    readJSON<string[]>(GROUPS_KEY, []),
  );
  useEffect(() => writeJSON(GROUPS_KEY, open), [open]);

  const toggle = useCallback((id: string) => {
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const ensureOpen = useCallback((id: string) => {
    setOpen((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  return {
    open,
    toggle,
    ensureOpen,
    isOpen: (id: string) => open.includes(id),
  };
}

/** Fullscreen toggle, with the vendor-prefixed fallbacks the app already relied on. */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
    ];
    events.forEach((e) => document.addEventListener(e, onChange));
    return () =>
      events.forEach((e) => document.removeEventListener(e, onChange));
  }, []);

  const toggle = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen', error);
    }
  }, []);

  return { isFullscreen, toggle };
}
